import { getLogger } from 'app-components/utils/core';
import { tracker } from 'app-components/utils/core';
import { useRef } from 'react';

const log = getLogger('useAudioMessage');

export const useAudioMessage = () => {
  const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);

  const handleAudioMessage = async (data: ArrayBuffer) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        nextStartTimeRef.current = audioContextRef.current.currentTime;
      }

      const audioContext = audioContextRef.current;

      const audioBuffer = await audioContext.decodeAudioData(data.slice(0));

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      // Schedule playback slightly in the future to prevent overlap
      const now = audioContext.currentTime;
      const startTime = Math.max(nextStartTimeRef.current, now + 0.05);
      source.start(startTime);
      sourcesRef.current.push(source);

      // Update next scheduled time
      nextStartTimeRef.current = startTime + audioBuffer.duration;
    } catch (err) {
      log.debug('[!] Failed to decode or schedule audio:', err);
    }
  };

  return handleAudioMessage;
};

const greetings = [
  "It's great to see you.",
  'Hello! How have you been?',
  'Hey there! Nice to see you.',
  "Hi! Hope you're having a good day.",
  "Greetings! It's wonderful to see you.",
  'Hey! Long time no see.',
  "Hi there! How's everything going?",
  'Hello! Always a pleasure to see you.',
  "Hey! It's nice to catch up with you.",
  'Hi! Hope all is well on your end.',
];
export const randomGreeting = () => greetings[Math.floor(Math.random() * greetings.length)];

export type SamplePrompt = {
  displayPrompt: string;
  actualPrompt: string;
};

export const trackSamplePromptClick = (prompt: SamplePrompt): void => {
  tracker.track(tracker.events.AIChatHomeQuestion, { displayPrompt: prompt.displayPrompt });
};

const samplePrompts: SamplePrompt[] = [
  // Agentic prompts
  {
    displayPrompt: '🔍 Find and fix uncategorized transactions',
    actualPrompt:
      '🔍 Find and fix uncategorized transactions by reviewing my transaction history from the past 3 months to identify transactions that are uncategorized or have incorrect categories based on the payee name, amount, or other details. Show me these transactions with suggested correct categories and help me update them.',
  },
  {
    displayPrompt: '💼 Set up a savings goal',
    actualPrompt:
      "💼 Set up a savings goal. Ask me what I'm saving for and the target amount, then recommend a weekly or monthly contribution plan to help me reach this goal on time.",
  },
  {
    displayPrompt: '🔔 Review bills for duplicates and changes',
    actualPrompt:
      '🔔 Review bills for duplicates and changes by analyzing all my recurring bills and subscriptions to identify any duplicate charges, unexpected price increases, or changes in billing frequency that I should be aware of.',
  },
  {
    displayPrompt: '👀 Help me create a spending watchlist',
    actualPrompt:
      '👀 Help me create a spending watchlist. Find and suggest me categories, tags or payees should I monitor. Help me set up watchlists with appropriate spending limits and alerts for the areas I choose.',
  },
  // Informational prompts
  {
    displayPrompt: '📊 How did spending categories change?',
    actualPrompt:
      '📊 How did spending categories change between this month and last? Compare my spending by category for the current month versus the previous month, showing the differences in amounts and percentages for each category.',
  },
  {
    displayPrompt: '🍽️ Which payees drive dining spend?',
    actualPrompt:
      '🍽️ Which payees drive dining spend? Analyze my dining category spending and identify the specific payees or restaurants that are contributing most to my dining expenses, especially those showing increases.',
  },
  {
    displayPrompt: "💵 What's my cash flow forecast?",
    actualPrompt:
      "💵 What's my cash flow forecast for the next two weeks? Project my expected income and expenses over the next two weeks based on my recurring income, scheduled bills, and typical spending patterns to show my anticipated cash flow.",
  },
  {
    displayPrompt: '📈 Which subscriptions went up in price?',
    actualPrompt:
      '📈 Which subscriptions went up in price recently? Compare my current subscription charges to their historical amounts and identify any subscriptions that have increased in price, showing the amount of increase and when it occurred.',
  },
  {
    displayPrompt: '📊 Investment performance this week',
    actualPrompt:
      '📊 Investment performance this week. Compare the current value and performance of my investment portfolio to where it was last week, showing gains or losses and any notable changes in individual holdings.',
  },
  {
    displayPrompt: "📊 What's driving my spending?",
    actualPrompt:
      "📊 What's driving my spending? Analyze my spending patterns and identify the main factors or categories that are driving my overall spending. Provide insights into which areas contribute most to my expenses and any trends you notice.",
  },
  {
    displayPrompt: '⚠️ Am I overspending?',
    actualPrompt:
      '⚠️ Am I overspending? Analyze my current spending compared to my income and any budgets I have set. Help me understand if I am overspending, and if so, which categories or areas are contributing to this. Provide specific recommendations on how to get back on track.',
  },
  {
    displayPrompt: '🗓️ How does this month look?',
    actualPrompt:
      '🗓️ How does this month look? Give me a comprehensive overview of my financial situation for this month, including my income, expenses, savings, and any notable patterns or concerns. Compare it to previous months if helpful.',
  },
  {
    displayPrompt: '✂️ Where can I cut back?',
    actualPrompt:
      '✂️ Where can I cut back? Review my spending and identify areas where I can realistically cut back on expenses. Prioritize recommendations based on potential savings impact and provide specific, actionable suggestions for reducing costs in those areas.',
  },
  {
    displayPrompt: '🔁 What subscriptions do I have?',
    actualPrompt:
      '🔁 What subscriptions do I have? Identify all my recurring subscriptions and recurring charges. List them out with their amounts and frequency, and help me understand the total monthly and annual cost of all my subscriptions combined.',
  },
  {
    displayPrompt: '❌ Any subscriptions I can cancel?',
    actualPrompt:
      '❌ Any subscriptions I can cancel? Review my recurring subscriptions and identify any that I might want to consider canceling. Look for duplicates, unused services, or subscriptions that may no longer be necessary, and help me understand the potential savings.',
  },
  {
    displayPrompt: '📅 What bills are coming up?',
    actualPrompt:
      '📅 What bills are coming up? Show me all the bills and recurring payments that are coming up in the near future, including their due dates and amounts, and help me understand my upcoming cash flow needs.',
  },
  {
    displayPrompt: '💰 Am I saving enough?',
    actualPrompt:
      '💰 Am I saving enough? Analyze my savings rate and compare it to my income and expenses. Help me understand if I am saving enough based on my financial goals, and provide recommendations on how much I should ideally be saving.',
  },
  {
    displayPrompt: '🎯 How close am I to my goals?',
    actualPrompt:
      '🎯 How close am I to my goals? Review my financial goals and show me my progress toward achieving them. Calculate how close I am to each goal, how much more I need to save, and provide insights on whether I am on track.',
  },
  {
    displayPrompt: '🧭 What should I focus on now?',
    actualPrompt:
      '🧭 What should I focus on now? Based on my current financial situation, help me prioritize what I should focus on right now. Consider my goals, spending patterns, upcoming expenses, and any areas that need immediate attention to provide actionable recommendations.',
  },
];

export const getRandomSamplePrompts = (count: number = 5): SamplePrompt[] => {
  const shuffled = [...samplePrompts].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, Math.min(count, samplePrompts.length));
};
